import React, { useState, useRef, useEffect } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useCompanyContext } from '../contexts/CompanyContext'

const API_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3001').replace('/api', '')

export const CompanySwitcher: React.FC = () => {
  const {
    multiCompanyEnabled,
    accessibleCompanies,
    selectedCompany,
    setSelectedCompany
  } = useCompanyContext()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  if (!multiCompanyEnabled || accessibleCompanies.length <= 1) {
    return null
  }

  const displayCompany = selectedCompany ?? accessibleCompanies[0]

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-navy-700/50 text-sm font-medium text-neutral-700 dark:text-neutral-300"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {displayCompany?.logoUrl ? (
          <img
            src={`${API_URL}${displayCompany.logoUrl.startsWith('/') ? '' : '/'}${displayCompany.logoUrl}`}
            alt=""
            className="h-5 w-5 object-contain rounded"
          />
        ) : (
          <div className="h-5 w-5 rounded bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
            <span className="text-primary-600 text-xs font-bold">
              {displayCompany?.name?.charAt(0) ?? '?'}
            </span>
          </div>
        )}
        <span className="max-w-[120px] truncate">{displayCompany?.name ?? 'Select company'}</span>
        <ChevronDownIcon className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute left-0 mt-1 py-1 w-48 bg-white dark:bg-navy-800 rounded-lg shadow-lg border border-neutral-200 dark:border-navy-600 z-50"
        >
          {accessibleCompanies.map((company) => (
            <li key={company.id} role="option">
              <button
                type="button"
                onClick={() => {
                  setSelectedCompany(company.id)
                  setOpen(false)
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-neutral-50 dark:hover:bg-navy-700/50 ${
                  selectedCompany?.id === company.id
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'text-neutral-700 dark:text-neutral-300'
                }`}
              >
                {company.logoUrl ? (
                  <img
                    src={`${API_URL}${company.logoUrl.startsWith('/') ? '' : '/'}${company.logoUrl}`}
                    alt=""
                    className="h-5 w-5 object-contain rounded"
                  />
                ) : (
                  <div className="h-5 w-5 rounded bg-primary-100 flex items-center justify-center">
                    <span className="text-primary-600 text-xs font-bold">{company.name.charAt(0)}</span>
                  </div>
                )}
                <span className="truncate">{company.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
